import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type DragEvent } from 'react';
import { api, errorMessage } from '@/lib/api';
import { formatBytes, formatDate, plural } from '@/lib/format';
import { useIsAdmin, useMe } from '@/lib/hooks';
import { openAdmin } from '@/lib/overlays';
import { useStore } from '@/lib/store';
import { toast, toastError } from '@/lib/toast';
import { Icon } from '@/ui/Icon';
import { Callout, Progress, Skeleton } from '@/ui/bits';
import { fileType, firstName, type FileItem, type FilesStorage, type RecordSheet } from '@shared/record';
import { keys, useFiles } from './queries';
import { cancelUpload, resumeUpload, retryUpload, startUploads, uploadStore, type UploadView } from './upload';
import { dayHeading } from './util';

// Files on a person or company, kept in the team's own S3 bucket. Uploads go straight from the browser to S3 in
// parts, so a file of any size works and a dropped connection only costs the part in flight. An upload interrupted
// by closing the page stays listed for the person uploading it, who can resume it by choosing the file again or
// cancel it. Everyone else sees a file once it has finished uploading.

function useUploads(sheet: RecordSheet): UploadView[] {
  return useStore(uploadStore, (s) => s.items).filter((u) => u.entityType === sheet.type && u.entityId === sheet.id);
}

function whenLabel(at: string): string {
  const day = dayHeading(at);
  return day === 'Today' || day === 'Yesterday' ? day.toLowerCase() : formatDate(at);
}

function FileTile({ name, mime }: { name: string; mime: string | null }) {
  const t = fileType(name, mime);
  return (
    <span className={`ft ${t.tone}`} aria-hidden="true">
      {t.label}
    </span>
  );
}

function UploadRow({ u }: { u: UploadView }) {
  const isAdmin = useIsAdmin();
  const pct = u.size ? Math.floor((u.loaded / u.size) * 100) : 0;
  const status =
    u.status === 'starting'
      ? 'Starting upload'
      : u.status === 'checking'
        ? `Checking the parts already in S3 · ${pct}%`
        : u.status === 'finishing'
        ? 'Joining the parts in S3'
        : u.status === 'uploading'
          ? `${pct}% · ${formatBytes(u.loaded)} of ${formatBytes(u.size)}`
          : null;
  return (
    <div className={`file up${u.status === 'failed' ? ' failed' : ''}`}>
      <FileTile name={u.name} mime={u.mime} />
      <span className="grow">
        <b>{u.name}</b>
        {u.status === 'failed' ? (
          <span className="up-err" role="alert">
            {u.error ?? 'The upload stopped.'}
          </span>
        ) : (
          <>
            <span aria-live="polite">{status}</span>
            <Progress
              value={u.status === 'finishing' ? 100 : pct}
              animated={u.status !== 'uploading' && u.status !== 'checking'}
              label={`Uploading ${u.name}`}
            />
          </>
        )}
        {u.status === 'failed' && u.cors && isAdmin ? (
          <button type="button" className="link up-fix" onClick={() => openAdmin('files')}>
            Open File storage
          </button>
        ) : null}
      </span>
      {u.status === 'failed' ? (
        <>
          <button type="button" className="btn btn-sm btn-tile" onClick={() => void retryUpload(u.key)}>
            <Icon name="refresh" />
            Retry
          </button>
          <button type="button" className="icon-btn xs" aria-label={`Stop uploading ${u.name}`} data-tip="Remove" onClick={() => cancelUpload(u.key)}>
            <Icon name="x" />
          </button>
        </>
      ) : (
        <button type="button" className="icon-btn xs" aria-label={`Cancel uploading ${u.name}`} data-tip="Cancel" onClick={() => cancelUpload(u.key)}>
          <Icon name="x" />
        </button>
      )}
    </div>
  );
}

function FileRow({ file, sheet, actions }: { file: FileItem; sheet: RecordSheet; actions: boolean }) {
  const qc = useQueryClient();
  const about = sheet.type === 'company' && file.person ? ` · ${firstName(file.person.name)}` : '';
  const meta = `${file.uploadedBy ? firstName(file.uploadedBy.name) : 'Someone'} · ${whenLabel(file.createdAt)}${about}`;

  const remove = async () => {
    try {
      const res = await api.post<{ ids: string[] }>('/bin', { type: 'file', ids: [file.id] });
      void qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) });
      toast(`${file.name} moved to the Bin`, {
        icon: 'trash',
        undo: () => {
          api
            .post('/bin/restore', { type: 'file', ids: res.ids })
            .then(() => qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) }))
            .catch((err) => toastError(err));
        },
      });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className="file">
      <FileTile name={file.name} mime={file.mime} />
      <span className="grow">
        <b title={file.name}>{file.name}</b>
        <span>{meta}</span>
      </span>
      <span className="sz">{formatBytes(file.size)}</span>
      <a className="icon-btn xs" href={`/api/files/${file.id}/download`} aria-label={`Download ${file.name}`} data-tip="Download">
        <Icon name="download" />
      </a>
      {actions ? (
        <button type="button" className="icon-btn xs" aria-label={`Delete ${file.name}`} data-tip="Delete" onClick={() => void remove()}>
          <Icon name="trash" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Your own upload that stopped when its page closed (or that is running in another window). Resume asks for the
 * file again, as the browser no longer has it; Cancel tells S3 to drop the parts sent so far.
 */
function InterruptedRow({ file, sheet, canResume }: { file: FileItem; sheet: RecordSheet; canResume: boolean }) {
  const qc = useQueryClient();
  const picker = useRef<HTMLInputElement>(null);
  const [cancelling, setCancelling] = useState(false);

  const cancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await api.post(`/files/${file.id}/abort`);
      await qc.invalidateQueries({ queryKey: keys.files(sheet.type, sheet.id) });
    } catch (err) {
      toastError(err);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="file pending">
      <FileTile name={file.name} mime={file.mime} />
      <span className="grow">
        <b title={file.name}>{file.name}</b>
        <span>Upload not finished · started {whenLabel(file.createdAt)}</span>
      </span>
      <span className="sz">{formatBytes(file.size)}</span>
      {canResume ? (
        <>
          <input
            ref={picker}
            type="file"
            hidden
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              e.target.value = '';
              if (picked) resumeUpload(qc, sheet.type, sheet.id, file, picked);
            }}
          />
          <button type="button" className="btn btn-sm btn-tile" onClick={() => picker.current?.click()}>
            <Icon name="upload" />
            Resume
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="icon-btn xs"
        aria-label={`Cancel uploading ${file.name}`}
        data-tip="Cancel"
        aria-disabled={cancelling || undefined}
        onClick={() => void cancel()}
      >
        {cancelling ? <span className="spin" aria-hidden="true" /> : <Icon name="x" />}
      </button>
    </div>
  );
}

function NotConnected({ storage }: { storage: FilesStorage }) {
  const isAdmin = useIsAdmin();
  const me = useMe();
  return (
    <Callout
      tone="info"
      icon="cloud"
      title="The team's file storage is not connected yet"
      actions={
        isAdmin ? (
          <button type="button" className="btn btn-sm btn-tile" onClick={() => openAdmin('files')}>
            <Icon name="cloud" />
            Open File storage
          </button>
        ) : null
      }
    >
      Files of any size go to your team's own Amazon S3 bucket.{' '}
      {isAdmin
        ? 'Connect the bucket in the admin console and uploads work from here.'
        : storage.admin && storage.admin.id !== me.user.id
          ? `${storage.admin.name} can connect it in the admin console.`
          : 'The super admin can connect it in the admin console.'}
    </Callout>
  );
}

function useDrop(onFiles: (files: FileList) => void, enabled: boolean) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const has = (e: DragEvent) => enabled && [...e.dataTransfer.types].includes('Files');
  return {
    over,
    props: {
      onDragEnter: (e: DragEvent) => {
        if (!has(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      },
      onDragOver: (e: DragEvent) => {
        if (!has(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      },
      onDragLeave: (e: DragEvent) => {
        if (!has(e)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      },
      onDrop: (e: DragEvent) => {
        if (!has(e)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      },
    },
  };
}

/** The Files tab: every file, uploads in progress, a drop zone and where the files are kept. */
export function FilesTab({ sheet }: { sheet: RecordSheet }) {
  const qc = useQueryClient();
  const q = useFiles(sheet.type, sheet.id);
  const uploads = useUploads(sheet);
  const input = useRef<HTMLInputElement>(null);
  const live = sheet.status === 'live';
  const configured = !!q.data?.storage.configured;
  const upload = (files: FileList) => startUploads(qc, sheet.type, sheet.id, files);
  const drop = useDrop(upload, configured && live);
  const busy = new Set(uploads.map((u) => u.fileId).filter(Boolean));
  const items = (q.data?.items ?? []).filter((f) => !busy.has(f.id));
  const count = items.filter((f) => f.status === 'ready').length;

  return (
    <section className={`tile files-box${drop.over ? ' drop-over' : ''}`} aria-labelledby={`files-${sheet.id}`} {...drop.props}>
      <div className="tile-h">
        <h3 id={`files-${sheet.id}`}>Files</h3>
        {q.data ? <span className="cnt">{plural(count, 'file')}</span> : null}
        {configured && live ? (
          <button type="button" className="btn btn-sm btn-tile" onClick={() => input.current?.click()}>
            <Icon name="upload" />
            Upload
          </button>
        ) : null}
      </div>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          if (e.target.files?.length) upload(e.target.files);
          e.target.value = '';
        }}
      />
      {q.isPending ? (
        <Skeleton rows={3} />
      ) : q.isError ? (
        <Callout
          tone="danger"
          title="The files did not load"
          actions={
            <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.refetch()}>
              <Icon name="refresh" />
              Try again
            </button>
          }
        >
          {errorMessage(q.error)}
        </Callout>
      ) : (
        <>
          {!configured ? <NotConnected storage={q.data.storage} /> : null}
          {uploads.length || items.length ? (
            <div className="files">
              {uploads.map((u) => (
                <UploadRow key={u.key} u={u} />
              ))}
              {items.map((f) =>
                f.status === 'ready' ? (
                  <FileRow key={f.id} file={f} sheet={sheet} actions={live} />
                ) : (
                  <InterruptedRow key={f.id} file={f} sheet={sheet} canResume={configured && live} />
                ),
              )}
            </div>
          ) : null}
          {configured && live ? (
            <>
              <div className={`dropzone${drop.over ? ' over' : ''}`}>
                <Icon name="upload" />
                {uploads.length || items.length ? (
                  <span>
                    Drop files here or{' '}
                    <button type="button" className="link" onClick={() => input.current?.click()}>
                      browse
                    </button>{' '}
                    · any size, stored in Amazon S3
                  </span>
                ) : (
                  <span>
                    Drop the first file here or{' '}
                    <button type="button" className="link" onClick={() => input.current?.click()}>
                      browse
                    </button>{' '}
                    · any size, stored in Amazon S3
                  </span>
                )}
              </div>
              <div className="s3-note">
                <Icon name="cloud" />
                {[q.data.storage.bucket, q.data.storage.region].filter(Boolean).join(' · ')}
              </div>
            </>
          ) : configured && !items.length ? (
            <p className="muted box-empty">No files here.</p>
          ) : null}
        </>
      )}
    </section>
  );
}

/** The side box: the latest few files, with a link to the tab. */
export function FilesBox({ sheet, onAll }: { sheet: RecordSheet; onAll: () => void }) {
  const q = useFiles(sheet.type, sheet.id);
  const uploads = useUploads(sheet);
  const ready = (q.data?.items ?? []).filter((f) => f.status === 'ready');
  if (!uploads.length && !ready.length) return null;
  return (
    <section className="tile" aria-labelledby={`fbox-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`fbox-${sheet.id}`}>Files</h3>
        <span className="cnt">
          {ready.length} · {formatBytes(ready.reduce((sum, f) => sum + f.size, 0))}
        </span>
        <button type="button" className="tile-more" onClick={onAll}>
          All
          <Icon name="chev-right" />
        </button>
      </div>
      <div className="files">
        {uploads.slice(0, 2).map((u) => (
          <UploadRow key={u.key} u={u} />
        ))}
        {ready.slice(0, Math.max(0, 3 - Math.min(2, uploads.length))).map((f) => (
          <FileRow key={f.id} file={f} sheet={sheet} actions={false} />
        ))}
      </div>
    </section>
  );
}
